import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi, type DriverEarning, type DriverEarningsStats } from '@/lib/adminApi';
import {
  Card,
  DataTable,
  Spinner,
  EmptyState,
  StatTile,
  type Column,
} from '@/components/ui';
import { IconReceipt } from '@/components/ui/icons';
import { AdminPage, AdminPageHeader, SearchBox } from '@/components/admin/shared';
import { dateTime, km } from '@/lib/adminFormat';
import { Pager } from '@/components/admin/Pager';
import { Drawer, Field } from '@/components/admin/Drawer';

const PAGE = 25;

/** Compact AUD formatter ($ + 2dp). */
function aud(n: number): string {
  return `$${(n ?? 0).toFixed(2)}`;
}

/** A best-effort suburb → suburb route label for an earning row. */
function routeLabel(e: DriverEarning): string {
  const a = (e.pickup_suburb || '').trim();
  const b = (e.drop_suburb || '').trim();
  if (a && b) return `${a} → ${b}`;
  return a || b || '—';
}

/** Driver Earnings — the per-trip earnings ledger, read from earnings-svc's
 *  driver_trip_earnings (earnings-svc OWNS it; every completed trip books one
 *  row off the Kafka trip.completed stream, with the tip bumped later if the
 *  rider tips). admin-svc only READS through the shared pool — this is a
 *  READ-ONLY audit view, exactly like the legacy Filament DriverEarningResource
 *  (which was append-only: create/edit/delete disabled). The per-trip `amount`
 *  is the driver's total take (fare + tip), mirroring the legacy
 *  `driver_earnings.amount` column. DEFENSIVE: shows a clean "not yet in
 *  Postgres" state when the table isn't in this DB. */
export function DriverEarnings() {
  const [q, setQ] = useState('');
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<DriverEarning | null>(null);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['admin', 'driver-earnings', q, offset],
    queryFn: ({ signal }) => adminApi.driverEarnings({ q, limit: PAGE, offset }, signal),
    placeholderData: (prev) => prev,
  });

  const columns: Array<Column<DriverEarning>> = [
    { key: 'trip', header: 'Trip', cell: (r) => <span className="metric text-xs text-muted">#{r.trip_id}</span> },
    {
      key: 'driver',
      header: 'Driver',
      cell: (r) => (
        <div className="min-w-0">
          <div className="truncate text-sm text-slate-100">{r.driver_name}</div>
          <div className="metric text-[11px] text-muted">#{r.driver_id}</div>
        </div>
      ),
    },
    {
      key: 'route',
      header: 'Route',
      cell: (r) => <span className="truncate text-xs text-slate-300">{routeLabel(r)}</span>,
    },
    {
      key: 'distance',
      header: 'Distance',
      align: 'right',
      cell: (r) => <span className="text-xs text-muted">{km(r.distance_km)}</span>,
    },
    {
      key: 'tip',
      header: 'Tip',
      align: 'right',
      cell: (r) =>
        r.tip > 0 ? (
          <span className="metric text-xs text-ok">{aud(r.tip)}</span>
        ) : (
          <span className="text-xs text-muted">—</span>
        ),
    },
    {
      key: 'amount',
      header: 'Earned',
      align: 'right',
      cell: (r) => (
        <span className="metric text-sm text-slate-100" title={`fare ${aud(r.fare)}${r.tip > 0 ? ` + tip ${aud(r.tip)}` : ''}`}>
          {aud(r.amount)}
        </span>
      ),
    },
    {
      key: 'when',
      header: 'Completed',
      cell: (r) => <span className="text-xs text-muted">{dateTime(r.completed_at)}</span>,
    },
    {
      key: 'view',
      header: '',
      align: 'right',
      cell: (r) => (
        <button
          type="button"
          onClick={() => setSelected(r)}
          className="rounded-md border border-ink-700 px-2 py-1 text-xs text-brand-400 hover:bg-ink-800"
        >
          View
        </button>
      ),
    },
  ];

  const unavailable = data && data.available === false;

  return (
    <AdminPage>
      <AdminPageHeader
        title="Driver Earnings"
        subtitle="Per-trip earnings ledger — what each driver was credited per completed trip (fare + tip). Read-only audit log."
        right={
          <SearchBox
            value={q}
            onChange={(v) => {
              setQ(v);
              setOffset(0);
            }}
            placeholder="Search by trip id / driver / suburb…"
          />
        }
      />

      {unavailable ? (
        <Card>
          <EmptyState
            title="Driver earnings not yet in Postgres"
            description={
              data?.note ??
              'The per-trip earnings ledger is owned by earnings-svc and is not in this Postgres yet. This page lights up automatically once it lands.'
            }
            icon={<IconReceipt className="h-6 w-6" />}
          />
        </Card>
      ) : (
        <>
          {data?.stats ? <EarningsStatsRow stats={data.stats} /> : null}

          <Card flush>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner label="Loading driver earnings…" />
              </div>
            ) : isError ? (
              <EmptyState title="Couldn't load driver earnings" description={(error as Error)?.message} />
            ) : (
              <>
                <DataTable<DriverEarning>
                  columns={columns}
                  rows={data?.items ?? []}
                  rowKey={(r) => String(r.trip_id)}
                  stickyHeader
                  empty={
                    <EmptyState
                      title="No earnings"
                      description="No per-trip earnings match this search. (A row is booked here when a trip completes.)"
                      icon={<IconReceipt className="h-6 w-6" />}
                    />
                  }
                />
                <Pager total={data?.total ?? 0} offset={offset} pageSize={PAGE} onOffset={setOffset} fetching={isFetching} />
              </>
            )}
          </Card>
        </>
      )}

      {selected ? <EarningDrawer earning={selected} onClose={() => setSelected(null)} /> : null}
    </AdminPage>
  );
}

/** The headline totals above the table: trip count + the fares / tips / total
 *  AUD split. Scoped to the current search filter so the header reconciles with
 *  the table. */
function EarningsStatsRow({ stats }: { stats: DriverEarningsStats }) {
  return (
    <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      <StatTile
        label="Trips"
        value={String(stats.trip_count)}
        hint={`earning${stats.trip_count === 1 ? '' : 's'} booked`}
      />
      <StatTile label="Fares" value={aud(stats.fares_dollars)} hint="driver fare cut" />
      <StatTile label="Tips" value={aud(stats.tips_dollars)} hint="100% to drivers" />
      <StatTile label="Total earned" value={aud(stats.total_dollars)} hint="fares + tips" />
    </div>
  );
}

/** EarningDrawer — the full per-trip earning breakdown. The list row already
 *  carries every field, so no extra fetch is needed for the body. */
function EarningDrawer({ earning: e, onClose }: { earning: DriverEarning; onClose: () => void }) {
  return (
    <Drawer
      title={`Trip #${e.trip_id}`}
      subtitle={`${e.driver_name} · earned ${aud(e.amount)}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        <div>
          <Field label="Trip" value={<span className="metric">#{e.trip_id}</span>} />
          <Field label="Driver" value={`${e.driver_name} (#${e.driver_id})`} />
          {e.phone ? <Field label="Phone" value={<span className="metric">{e.phone}</span>} /> : null}
          {e.email ? <Field label="Email" value={e.email} /> : null}
          <Field label="Route" value={routeLabel(e)} />
          <Field label="Distance" value={km(e.distance_km)} />
          <Field label="Fare (driver cut)" value={<span className="metric">{aud(e.fare)}</span>} />
          <Field
            label="Tip"
            value={e.tip > 0 ? <span className="metric text-ok">{aud(e.tip)}</span> : <span className="text-muted">—</span>}
          />
          <Field label="Total earned" value={<span className="metric text-slate-100">{aud(e.amount)}</span>} />
          <Field label="Completed" value={dateTime(e.completed_at)} />
        </div>
      </div>
    </Drawer>
  );
}
